import { StreamChat } from 'stream-chat'
import { Streami18n } from 'stream-chat-react'
import { italianTranslations, italianDateConfig } from '../settings/streamChatTranslations'
import streamChatConfig from '../settings/streamChatConfig'

// Accepted attachment types for the chat composer — images AND documents.
// Stream validates this list as MIME types, so extensions must not be included
// here (they are otherwise skipped with a browser warning).
export const CHAT_ACCEPTED_FILE_TYPES = [
	// Images
	'image/png',
	'image/jpeg',
	'image/webp',
	'image/heic',
	'image/heif',
	// Documents
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'text/plain',
	'text/csv',
]

class StreamChatService {
	constructor() {
		this.client = null
		this.user = null
		this.isConnected = false
		this.connectionState = 'idle'
		this.connectionError = null
		this.connectionSubscribers = new Set()
		this.connectionListenersClient = null
		this.i18nInstance = null
		this.setupI18n()
	}

	setConnectionState(connectionState, error = null) {
		this.connectionState = connectionState
		this.connectionError = error
		this.isConnected = connectionState === 'online'
		this.connectionSubscribers.forEach((subscriber) => {
			try {
				subscriber({ state: connectionState, error })
			} catch (_error) {
				// A UI subscriber must never interrupt Stream's connection events.
			}
		})
	}

	getConnectionState() {
		return {
			state: this.connectionState,
			error: this.connectionError,
		}
	}

	subscribeToConnectionState(subscriber) {
		if (typeof subscriber !== 'function') return () => {}
		this.connectionSubscribers.add(subscriber)
		subscriber(this.getConnectionState())
		return () => this.connectionSubscribers.delete(subscriber)
	}

	isSocketHealthy(client = this.client) {
		return Boolean(
			client?.userID &&
				(client?.wsConnection?.isHealthy ||
					client?.wsFallback?.isHealthy?.())
		)
	}

	attachConnectionListeners(client) {
		if (!client || this.connectionListenersClient === client) return

		if (this.connectionListenersClient) {
			this.connectionListenersClient.off(
				'connection.changed',
				this.handleConnectionChanged
			)
			this.connectionListenersClient.off(
				'connection.recovered',
				this.handleConnectionRecovered
			)
		}

		this.handleConnectionChanged = (event) => {
			if (!client.userID) {
				this.setConnectionState('idle')
				return
			}
			if (event?.online) {
				this.setConnectionState('online')
				return
			}
			const browserOffline =
				typeof navigator !== 'undefined' && navigator.onLine === false
			this.setConnectionState(browserOffline ? 'offline' : 'reconnecting')
		}
		this.handleConnectionRecovered = () => {
			this.setConnectionState('online')
		}

		client.on('connection.changed', this.handleConnectionChanged)
		client.on('connection.recovered', this.handleConnectionRecovered)
		this.connectionListenersClient = client
	}

	withTimeout(promise, timeoutMs, message) {
		let timeout
		return Promise.race([
			Promise.resolve(promise),
			new Promise((_, reject) => {
				timeout = setTimeout(() => reject(new Error(message)), timeoutMs)
			}),
		]).finally(() => clearTimeout(timeout))
	}

	/**
	 * Setup Italian localization for Stream Chat
	 */
	setupI18n() {
		this.i18nInstance = new Streami18n({
			language: 'it',
			translationsForLanguage: italianTranslations,
			dayjsLocaleConfigForLanguage: italianDateConfig
		})
	}

	/**
	 * Initialize the Stream Chat client
	 * @param {string} apiKey - API key of Stream Chat
	 * @param {Object} userData - User data
	 * @param {string} userToken - User authentication token
	 */
	async initialize(apiKey, userData, userToken) {
		try {
			// Reuse an existing live connection for the same user. The unread badge
			// keeps a global connection open app-wide; when the Chat page mounts it
			// should reuse it rather than tear it down and reconnect (which would
			// drop the unread listeners and flicker the socket).
			if (
				this.client &&
				this.user?.id &&
				userData?.id &&
				this.user.id === userData.id &&
				this.isSocketHealthy()
			) {
				this.setConnectionState('online')
				return this.client
			}

			// De-dupe concurrent connects for the same user. The global bootstrap
			// and the Chat page can both call initialize() near-simultaneously on
			// first load; without this they'd fire two connectUser() calls and the
			// socket could end up in a bad state (isConnected flapping), which makes
			// downstream guards like isClientConnected() intermittently false.
			if (this._connectPromise && this._connectingUserId === userData?.id) {
				await this._connectPromise
				return this.client
			}

			// A previously connected user keeps its identity while Stream repairs
			// the socket. Reopen that connection instead of calling connectUser()
			// again with the same user.
			if (
				this.client?.userID === userData?.id &&
				this.user?.id === userData?.id
			) {
				this.setConnectionState('reconnecting')
				this._connectingUserId = userData?.id
				this._connectPromise = this.withTimeout(
					this.client.openConnection(),
					15000,
					'Timeout durante la riconnessione alla chat'
				)
				await this._connectPromise
				this._connectPromise = null
				this.setConnectionState('online')
				return this.client
			}

			if (this.client?.userID) {
				await this.disconnect()
			}

			this.client = StreamChat.getInstance(apiKey, streamChatConfig.client)
			this.attachConnectionListeners(this.client)

			// Chat supports both image AND document attachments (support chats, group
			// chats, DMs). The file-input `accept` is driven by `acceptedFiles`; applies
			// to all MessageComposer instances (channel, thread, edit).
			// Note: MessageComposer has no `acceptedFiles` setter; config lives under
			// `attachments` (see stream-chat AttachmentManager / updateConfig).
			this.client.setMessageComposerSetupFunction(({ composer }) => {
				composer.updateConfig({
					attachments: { acceptedFiles: CHAT_ACCEPTED_FILE_TYPES },
				})
			})

			// Connect user (track the in-flight promise so concurrent callers
			// for the same user await it instead of firing a second connect).
			this.setConnectionState('connecting')
			this._connectingUserId = userData?.id
			this._connectPromise = this.client.connectUser(userData, userToken)
			await this._connectPromise
			this._connectPromise = null

			this.user = userData
			this.setConnectionState('online')

			return this.client
		} catch (error) {
			this._connectPromise = null
			this.setConnectionState(
				typeof navigator !== 'undefined' && navigator.onLine === false
					? 'offline'
					: 'error',
				error
			)
			console.error('Error initializing Stream Chat:', error)
			throw error
		}
	}

	/**
	 * Disconnect the user from Stream Chat
	 */
	async disconnect() {
		try {
			if (this.client?.userID) {
				await this.client.disconnectUser()
			}
			this.user = null
			this.setConnectionState('idle')
		} catch (error) {
			console.error('Error disconnecting user:', error)
			this.setConnectionState('error', error)
		}
	}

	async reconnect() {
		if (!this.client?.userID) {
			throw new Error('Utente chat non inizializzato')
		}
		if (this.isSocketHealthy()) {
			this.setConnectionState('online')
			return this.client
		}
		if (this._connectPromise) {
			await this._connectPromise
			return this.client
		}

		try {
			this.setConnectionState('reconnecting')
			this._connectPromise = this.withTimeout(
				this.client.openConnection(),
				15000,
				'Timeout durante la riconnessione alla chat'
			)
			await this._connectPromise
			this.setConnectionState('online')
			return this.client
		} catch (error) {
			this.setConnectionState(
				typeof navigator !== 'undefined' && navigator.onLine === false
					? 'offline'
					: 'error',
				error
			)
			throw error
		} finally {
			this._connectPromise = null
		}
	}

	async watchChannel(channel) {
		if (!channel || typeof channel.watch !== 'function') {
			throw new Error('Canale chat non valido')
		}
		if (!this.isSocketHealthy()) {
			await this.reconnect()
		}
		return this.withTimeout(
			channel.watch(),
			12000,
			'Timeout durante il caricamento della conversazione'
		)
	}

	/**
	 * Whether a private chat is open to every plan: a member's chat with the FREE
	 * Program Tutor (`tutor_chat: true`) or one a badge holder started
	 * (`badge_chat: true`, client 21/09/2026). The backend marks those Stream
	 * channels; chat lists built from backend groups don't carry the flags, so
	 * read them from Stream without watching.
	 * @param {string} channelId - Stream channel id (the group's provider id)
	 * @returns {Promise<boolean>}
	 */
	async isOpenToEveryPlan(channelId) {
		if (!this.client || !channelId) return false
		const [channel] = await this.client.queryChannels(
			{ cid: `messaging:${channelId}` },
			{},
			{ limit: 1, watch: false, state: false, presence: false }
		)
		return channel?.data?.tutor_chat === true || channel?.data?.badge_chat === true
	}

	/**
	 * Get the channels of the user
	 * @param {Object} filter - Filter for the channels
	 * @param {Object} sort - Sort for the channels
	 * @param {Object} options - Additional options
	 */
	async getChannels(filter = null, sort = null, options = {}) {
		try {
			if (!this.client || !this.isConnected) {
				throw new Error('Client not initialized or not connected')
			}

			const defaultFilter = filter || { members: { $in: [this.client.userID] } }
			const defaultSort = sort || { last_message_at: -1 }
			const defaultOptions = { watch: true, state: true, ...options }

			const channels = await this.client.queryChannels(defaultFilter, defaultSort, defaultOptions)
			return channels
		} catch (error) {
			console.error('Error getting channels:', error)
			throw error
		}
	}

	/**
	 * Get Stream Chat channels based on backend groups list
	 * @param {Array} backendGroups - List of groups from backend
	 */
	async getChannelsFromBackendGroups(backendGroups) {
		try {
			if (!this.client || !this.isConnected) {
				throw new Error('Client not initialized or not connected')
			}

			if (backendGroups.length === 0) {
				return []
			}

			// Separate groups by membership status
			const memberGroups = backendGroups.filter(group => group.isMember === true)
			const nonMemberGroups = backendGroups.filter(group => group.isMember !== true)

			// Query Stream Chat only for groups where user is a member
			let streamChannels = []
			if (memberGroups.length > 0) {
				const memberGroupIds = memberGroups.map(group => group.providerGroupId)
				const filter = {
					id: { $in: memberGroupIds },
					members: { $in: [this.client.userID] }
				}
				
				const sort = { last_message_at: -1 }
				const options = { watch: true, state: true }

				streamChannels = await this.client.queryChannels(filter, sort, options)
			}

			// Create a map of Stream Chat channels by ID for quick lookup
			const streamChannelsMap = new Map()
			streamChannels.forEach(channel => {
				streamChannelsMap.set(channel.id, channel)
			})

			// Process member groups: merge Stream Chat data with backend data
			const memberChannels = memberGroups.map(backendGroup => {
				const streamChannel = streamChannelsMap.get(backendGroup.providerGroupId)
				
				if (streamChannel) {
					// Channel exists in Stream Chat, add backendData without losing methods
					// Don't use spread operator as it loses methods - assign backendData directly
					streamChannel.backendData = backendGroup
					return streamChannel
				} else {
					// Channel should exist but doesn't (sync issue), create placeholder
					console.warn('Member group without Stream Chat channel:', backendGroup.providerGroupId)
					return this.createPlaceholderChannel(backendGroup)
				}
			})

			// Process non-member groups: create placeholders with only backend data
			const nonMemberChannels = nonMemberGroups.map(backendGroup => {
				return this.createPlaceholderChannel(backendGroup)
			})

			// Combine all channels
			const allChannels = [...memberChannels, ...nonMemberChannels]
			return allChannels
		} catch (error) {
			console.error('Error getting channels from backend groups:', error)
			throw error
		}
	}

	/**
	 * Create a placeholder channel object with only backend data
	 * Used for channels where user is not a member
	 * @param {Object} backendGroup - Backend group data
	 */
	createPlaceholderChannel(backendGroup) {
		return {
			id: backendGroup.providerGroupId,
			type: 'messaging',
			data: {
				name: backendGroup.name,
				image: backendGroup.imageUrl
			},
			state: {
				messages: [],
				membership: null, // User is not a member
				members: {}
			},
			// Add methods that might be called
			getConfig: () => ({}),
			countUnread: () => 0,
			lastRead: () => null,
			watch: async () => {},
			backendData: backendGroup
		}
	}

	/**
	 * Create a new channel
	 * @param {string} type - Type of channel ('messaging', 'team', etc.)
	 * @param {string} id - ID of the channel (optional)
	 * @param {Object} data - Data of the channel
	 */
	async createChannel(type = 'messaging', id = null, data = {}) {
		try {
			if (!this.client || !this.isConnected) {
				throw new Error('Client not initialized or not connected')
			}

			const channelId = id || `channel_${Date.now()}`
			const channel = this.client.channel(type, channelId, {
				created_by_id: this.client.userID,
				...data
			})

			await channel.create()
			return channel
		} catch (error) {
			console.error('Error creating channel:', error)
			throw error
		}
	}

	/**
	 * Search for available users
	 * @param {string} query - Search query
	 * @param {Object} filter - Additional filter
	 */
	async searchUsers(query = '', filter = {}) {
		try {
			if (!this.client || !this.isConnected) {
				throw new Error('Client not initialized or not connected')
			}

			const searchFilter = {
				...filter,
				...(query && {
					$or: [
						{ name: { $autocomplete: query } },
						{ id: { $autocomplete: query } }
					]
				})
			}

			const response = await this.client.queryUsers(searchFilter, { name: 1 })
			return response.users
		} catch (error) {
			console.error('Error searching users:', error)
			throw error
		}
	}

	/**
	 * Get the Stream Chat client
	 */
	getClient() {
		return this.client
	}

	/**
	 * Block/mute a user (Apple Guideline 1.2). blockUser prevents the target
	 * from messaging (1:1), muteUser hides their messages in shared channels.
	 * Both are best-effort and no-op if the client isn't connected.
	 */
	async blockUser(targetStreamUserId) {
		if (!this.client || !targetStreamUserId) return
		try {
			await this.client.blockUser(targetStreamUserId)
		} catch (e) {
			console.warn('[streamChat] blockUser failed', e?.message)
		}
		try {
			await this.client.muteUser(targetStreamUserId)
		} catch (e) {
			console.warn('[streamChat] muteUser failed', e?.message)
		}
	}

	async unblockUser(targetStreamUserId) {
		if (!this.client || !targetStreamUserId) return
		try {
			await this.client.unBlockUser(targetStreamUserId)
		} catch (e) {
			console.warn('[streamChat] unBlockUser failed', e?.message)
		}
		try {
			await this.client.unmuteUser(targetStreamUserId)
		} catch (e) {
			console.warn('[streamChat] unmuteUser failed', e?.message)
		}
	}

	/**
	 * Check if the client is connected
	 */
	isClientConnected() {
		return this.isSocketHealthy() && this.client
	}

	/**
	 * Get the current user information
	 */
	getCurrentUser() {
		return this.user
	}

	/**
	 * Get the i18n instance for localization
	 */
	getI18nInstance() {
		return this.i18nInstance
	}

	/**
	 * Find an online operator from a list of operator IDs
	 * @param {Array<string>} operatorIds - Array of operator Stream Chat user IDs
	 * @returns {Object|null} - First online operator found, or null if none are online
	 */
	async findOnlineOperator(operatorIds) {
		try {
			if (!this.client || !this.isConnected) {
				throw new Error('Client not initialized or not connected')
			}

			if (!operatorIds || operatorIds.length === 0) {
				return null
			}

			// Query users to check their presence/online status
			const response = await this.client.queryUsers({
				id: { $in: operatorIds }
			})

			// Find first online operator
			// Stream Chat users have an 'online' property or presence.status
			const onlineOperator = response.users.find(user => {
				// Check if user is online
				return user.online === true || 
				       (user.presence && user.presence.status === 'online')
			})

			return onlineOperator || null
		} catch (error) {
			console.error('Error finding online operator:', error)
			throw error
		}
	}

	/**
	 * Create a support channel with an online operator
	 * Uses 'support' channel type to distinguish from regular messaging channels
	 * @param {string} operatorId - Stream Chat user ID of the operator
	 * @param {string} userName - Full name of the user creating the channel (e.g., "Mario Rossi")
	 * @returns {Object} - Created channel
	 */
	async createSupportChannel(operatorId, userName = '') {
		try {
			if (!this.client || !this.isConnected) {
				throw new Error('Client not initialized or not connected')
			}

			if (!this.client.userID) {
				throw new Error('User not connected to Stream Chat')
			}

			// Create a support channel (type: 'support') between user and operator
			// Use a unique ID based on user and operator IDs to ensure consistency
			const channelId = `support-${this.client.userID}-${operatorId}`
			
			// Channel name includes user's full name for easier identification
			const channelName = userName && userName.trim() 
				? `Supporto per ${userName.trim()}`
				: 'Supporto'
			
			const channel = this.client.channel('support', channelId, {
				members: [this.client.userID, operatorId],
				name: channelName
			})

			await channel.create()
			await this.watchChannel(channel)

			return channel
		} catch (error) {
			console.error('Error creating support channel:', error)
			throw error
		}
	}

	/**
	 * Get support channels from Stream Chat
	 * Used by staff users (with badge) to see all support conversations
	 * @param {Object} options - Additional options for querying
	 * @returns {Array} - Array of support channels
	 */
	async getSupportChannels(options = {}) {
		try {
			if (!this.client || !this.isConnected) {
				throw new Error('Client not initialized or not connected')
			}

			// Query channels of type 'support' where the current user is a member
			const filter = {
				type: 'support',
				members: { $in: [this.client.userID] }
			}

			const sort = { last_message_at: -1 }
			const queryOptions = { watch: true, state: true, ...options }

			const channels = await this.client.queryChannels(filter, sort, queryOptions)
			return channels
		} catch (error) {
			console.error('Error getting support channels:', error)
			throw error
		}
	}
}

// Singleton instance of the service
const streamChatService = new StreamChatService()

export default streamChatService

/**
 * Custom hook to use Stream Chat in React components
 */
export const useStreamChat = () => {
	return {
		client: streamChatService.getClient(),
		isConnected: streamChatService.isClientConnected(),
		user: streamChatService.getCurrentUser(),
		i18nInstance: streamChatService.getI18nInstance(),
		initialize: streamChatService.initialize.bind(streamChatService),
		disconnect: streamChatService.disconnect.bind(streamChatService),
		reconnect: streamChatService.reconnect.bind(streamChatService),
		watchChannel: streamChatService.watchChannel.bind(streamChatService),
		connectionState: streamChatService.getConnectionState(),
		getChannels: streamChatService.getChannels.bind(streamChatService),
		getChannelsFromBackendGroups: streamChatService.getChannelsFromBackendGroups.bind(streamChatService),
		createChannel: streamChatService.createChannel.bind(streamChatService),
		searchUsers: streamChatService.searchUsers.bind(streamChatService),
		findOnlineOperator: streamChatService.findOnlineOperator.bind(streamChatService),
		createSupportChannel: streamChatService.createSupportChannel.bind(streamChatService),
		getSupportChannels: streamChatService.getSupportChannels.bind(streamChatService),
	}
}
